/**
 * Ключи кэша react-query — одним списком.
 *
 * Ключ пишется в двух местах: там, где запрос объявлен, и там, где кэш
 * сбрасывают после записи. Пока они были строками по файлам, любая опечатка
 * означала молча неработающее обновление экрана. Здесь ключи собраны в одном
 * месте, а хвост можно опустить: `keys.group(id)` совпадает по префиксу с
 * `keys.group(id, languageId)`, чего и ждёт `invalidateQueries`.
 */

function key(...parts: unknown[]): unknown[] {
	const at = parts.findIndex(part => part === undefined)
	return at < 0 ? parts : parts.slice(0, at)
}

export const keys = {
	groups: (languageId?: number) => key("groups", languageId),
	group: (id?: string, languageId?: number) => key("group", id, languageId),
	/** Поиск групп по товарам внутри них — ответ рижского BFF `/ai/groups/by-product`. */
	groupsByProduct: (query?: string, languageId?: number) =>
		key("groups-by-product", query, languageId),
	groupRows: (id?: string, languageId?: number) => key("group-rows", id, languageId),
	groupCategories: (id?: string, languageId?: number) =>
		key("group-categories", id, languageId),
	groupCatalogs: (id?: string) => key("group-catalogs", id),
	groupImages: (id?: string) => key("group-images", id),
	groupPictograms: (id?: string, languageId?: number) =>
		key("group-pictograms", id, languageId),
	pictograms: (languageId?: number) => key("pictograms", languageId),

	/** Состав набора (комплектация). Ключ идёт от группы: сброс после любой
	 *  записи в карточку задевает и его, даже когда toolsetId ещё не известен. */
	toolsetProducts: (groupId?: string, toolsetId?: string, languageId?: number) =>
		key("toolset-products", groupId, toolsetId, languageId),
	possibleToolsetProducts: (
		groupId?: string,
		toolsetId?: string,
		languageId?: number
	) => key("possible-toolset-products", groupId, toolsetId, languageId),
	reservedGroupId: () => key("reserved-group-id"),

	freeProducts: (priceGroupId?: number | null, languageId?: number) =>
		key("free-products", priceGroupId, languageId),
	attributes: (languageId?: number) => key("attributes", languageId),
	imageTypes: () => key("image-types"),
	languages: () => key("languages"),
	priceGroups: () => key("price-groups"),
	catalogs: () => key("catalogs"),
	websites: () => key("websites"),
	webCategories: (languageId?: number) => key("web-categories", languageId),

	translates: (catalogId?: number | null, source?: number, languageId?: number) =>
		key("translates", catalogId, source, languageId),
	translateUrls: (
		catalogId?: number | null,
		websiteId?: number | null,
		languageId?: number
	) => key("translate-urls", catalogId, websiteId, languageId),

	/** Состояние группы на сайте — ответ рижского BFF `/ai/site/group-status`. */
	siteStatus: (productGroupId?: string | null, websiteId?: number | null) =>
		key("site-status", productGroupId, websiteId),
	notReadyToDeploy: (catalogId?: number | null, websiteId?: number | null) =>
		key("not-ready-to-deploy", catalogId, websiteId),

	/** Состав каталога — ответ рижского BFF `/ai/catalog/groups`. */
	catalogGroups: (catalogId?: number, languageId?: number) =>
		key("catalog-groups", catalogId, languageId),
	/** Печатные каталоги, заведённые через форму создания на `/publish`. */
	printedCatalogIds: () => key("printed-catalog-ids"),

	/** Данные генерации каталога — `GET /api/product-group/guide`. Ключ на все
	 *  три параметра запроса: разные языки и сайты дают разные ответы. */
	guide: (catalogId?: number | null, websiteId?: number | null, languageId?: number) =>
		key("guide", catalogId, websiteId, languageId),

	/** Ссылки групп каталога на сайте — ответ рижского BFF `/ai/site/catalog-urls`
	 *  (источник `urlQr` для QR-кодов `/pdf`, см. `screens/pdf/groupUrls.ts`).
	 *  От языка не зависит — те же ссылки для всех языков генерации. */
	catalogUrls: (catalogId?: number | null, websiteId?: number | null) =>
		key("catalog-urls", catalogId, websiteId),
}
